import { useNavigate } from "react-router-dom";
import { WarningIcon } from "../icon/warning-icon";
import { getFullDate } from "../utils/helper.util";
import { InfoIcon } from "lucide-react";
import { formatDateWithOrdinal } from "~/app/licenses/[licenseId]/page";
import linkedProbe from "~/assets/images/linked-probe.png";
import { PaymentsIcon } from "../icon/payments-icon";
import { useEffect, useState } from "react";
import useAuthStore from "~/store/auth.store";
import { networkService } from "~/services";
import { CircleCrossIcon } from "../icon/circle-cross-icon";

// Function to determine model from serial number
// const getModelFromSerialNumber = (serialNumber: string) => {
//   if (!serialNumber) return "Probe unknown";
//   const prefix = serialNumber.substring(0, 2).toUpperCase();
//   if (prefix === "BT") {
//     return "iQ+";
//   } else if (prefix === "KB") {
//     return "iQ3";
//   } else {
//     return "Probe unknown";
//   }
// };

// Function to determine if serial number should be displayed
const shouldDisplaySerialNumber = (serialNumber: string) => {
  if (!serialNumber) return false;
  const prefix = serialNumber.substring(0, 2).toUpperCase();
  return prefix === "BT" || prefix === "KB";
};

interface PaymentMethodData {
  last4: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  nextPayment?: {
    date: string;
    amount: number;
    currency: string;
  };
}

interface LicenseCardProps {
  licenseInfo: any;
  probeInfo: any;
  user?: any;
}

const LicenseCard: React.FC<LicenseCardProps> = ({
  licenseInfo,
  probeInfo,
  user,
}) => {
  const navigate = useNavigate();
  const probe = probeInfo.find((probe) => probe.id === licenseInfo?.device?.id);
  const isTrial = licenseInfo?.isTrial === true;
  const showNonRenewed = licenseInfo.isTrial === false && licenseInfo.autoRenewActive === false && licenseInfo.isActive === true;
  const isPaymentFailed = !!licenseInfo?.paymentFailedAt; // Force payment failed for all cards
  const expiryDate = licenseInfo.expiryDate ? formatDateWithOrdinal(licenseInfo.expiryDate) : "-";
  const cancelledAt = licenseInfo.cancelledAt ? formatDateWithOrdinal(licenseInfo.cancelledAt) : "-";

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodData | null>(null);
  const { user: authUser } = useAuthStore((state) => ({ user: state.user }));

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    navigate(`/licenses/${licenseInfo.id}`);
  };

  useEffect(() => {
    const fetchPaymentMethod = async () => {
      try {
        if (authUser?.organizationId) {
          const response = await networkService.get<any>(`/organizations/${authUser.organizationId}/payment-method`);
          console.log("Response:", response); // Add this line to log the response
          if (response.statusCode === "10000") {
            setPaymentMethod(response.data);
          }
        }
      } catch (error) {
        console.error("Failed to fetch payment method:", error);
      }
    };

    fetchPaymentMethod();
  }, [authUser?.organizationId]);

  // Info banner logic
  let infoBanner = null;
  if ((isTrial && licenseInfo?.device === null) || (!isTrial && licenseInfo?.device === null)) {
    // Show the "add probe" banner for both trial and non-trial with no device
    infoBanner = (
      <div className="bg-[#FDF5EF] border border-[#F3CCB5] text-[#CC462A] rounded-md px-6 py-4 mb-4 flex items-center justify-between">
        <div className="flex md:items-center w-full flex-col md:flex-row justify-between gap-2">
          <div className="flex gap-2 items-start">
            <span><InfoIcon size={18} /></span>
            <span className="font-normal">You won't be able to use HeartFocus without adding your probe serial number.</span>
          </div>
          <div>
            <button className="bg-black-900 text-white-a700 px-4 py-2 rounded-full shadow-xl mt-4"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/probes/update-probe-details/${user.organizationId}/new?licenseId=${licenseInfo?.id}`
                );
              }}
            >Link probe</button>
          </div>
        </div>
      </div>
    );
  } else if (isTrial && licenseInfo?.device !== null) {
    // Show the "charged on" banner for trial with device
    infoBanner = (
      <div className="bg-[#EDEDFA] border border-[#C4C6EE] text-[#5B4DB4] rounded-md px-6 py-4 md:mb-4 flex items-center justify-between">
        <div className="flex gap-2 items-start">
          <span><InfoIcon size={18} /></span>
          <span className="font-normal">You will be automatically charged on <span>{expiryDate}</span>. We will send you a reminder before your trial ends.</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="border shadow-md w-full rounded-[10px] px-6 py-7 md:py-7 md:px-8 font-space-grotesk md:min-h-fit md:max-w-full cursor-pointer hover:shadow-lg transition-shadow"
      onClick={handleCardClick}
    >
      {/* license info */}
      <section className="flex flex-col gap-4 pb-5">
        <h1 className="flex flex-col md:flex-row justify-between font-bold text-2xl">
          <span>
            {licenseInfo?.subscription?.plan?.name ??
              `License-${licenseInfo?.id}`}
          </span>
          <div className="flex items-center gap-2 md:ml-auto mt-2">
            {isPaymentFailed ? (
              <span className="bg-[#FFF5F5] border border-[#F5C6C6] text-[#E27D55] text-sm font-medium px-2 py-1.5 rounded-sm h-9">
                Payment failed
              </span>
            ) : (
              <>
                {showNonRenewed && (
                  <span className="bg-[#FDEDF2] border border-[#F5C6C6] text-[#C94A6A] text-sm font-medium px-2 py-1.5 rounded-sm h-9">
                    Non-renewed
                  </span>
                )}
                {isTrial && (
                  <span className="bg-[#EDEDFA] border border-[#C4C6EE] text-[#807CD6] text-sm font-medium px-2 py-1.5 rounded-sm h-9">
                    Free Trial
                  </span>
                )}
                <span
                  className={`text-sm font-medium px-2 py-1.5 rounded-sm h-9 
        ${licenseInfo?.isActive
                      ? "bg-[#F2FBF8] border border-[#A9E6D3] text-[#4BB69C]"  // Green for Active
                      : "bg-[#FFF5F5] border border-[#F5C6C6] text-[#E27D55]"} // Red for Inactive
      `}
                >
                  {licenseInfo?.isActive ? "Active" : "Inactive"}
                </span>
              </>
            )}
          </div>
        </h1>
        {/* Payment Failed Banner */}
        {isPaymentFailed && (
          <div className="bg-[#FFF5F5] border border-[#F5C6C6] text-[#E27D55] rounded-md px-6 py-4 mb-4 flex items-center justify-between">
            <div className="flex gap-2">
              <span className="text-lg">&#9888;</span>
              <span className="font-medium">The payment of your license failed. Please update your payment method within 7 days to <br />
                reactivate your license.</span>
            </div>
            <button
              className="w-40 ml-6 text-sm py-2 rounded-full bg-black-900 text-white-a700 font-semibold shadow hover:bg-[#23232a] transition"
              onClick={e => {
                e.stopPropagation();
                navigate('/billing-details-page');
              }}
            >
              Update payment
            </button>
          </div>
        )}
        {/* Info Banner (conditional) */}
        {infoBanner}
        {/* <div className="flex md:flex-row flex-col justify-between text-zinc-600 font-normal">
          <span className="text-left flex gap-1 max-w-[300px] text-wrap">
            Id: {licenseInfo?.id}
          </span>
          <span className="lg:flex lg:flex-col mt-2 md:mt-0 md:text-right">
            <span>{isTrial ? "Trial Expires on " : "Expires on "}</span>
            <span className="font-semibold">{licenseInfo?.expiryDate
              ? getFullDate(licenseInfo?.expiryDate)
              : "Not Available"}
            </span>
          </span>
        </div> */}
      </section>
      {/* probe info */}
      {/* <section className="min-h-[100px]">
        {licenseInfo?.device ? (
          <div className="flex flex-col justify-between min-h-[80px]">
            <div className="flex flex-col gap-2 font-medium text-base">
              <p>
                {`${probe?.nickName} attached (${probe?.model?.manufacturer?.name}, ${probe?.model?.modelName})`}
              </p>
              <p className="font-normal text-xs text-gray-500">{`Serial Number : ${probe?.externalDeviceId}`}</p>
            </div>
            <div className="flex md:flex-row flex-row mt-5 md:mt-0 md:ml-auto gap-4 md:gap-3">
              <button
                className="text-center font-medium px-4 rounded-full bg-white-a700 text-gray-900 lg:text-[14px] max-[400px]:text-[13px] max-[380px]:text-[12px] md:text-base border border-gray-300"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/probes/update-probe-details/${user.organizationId}/${probe.id}`
                  );
                }}>
                Edit probe
              </button>
              <div className="flex justify-center items-center bg-angular-gradient rounded-full p-0.5">
                <button
                  type="submit"
                  className="bg-rounded-full w-[121px] h-[34px] text-[14px] font-space-grotesk font-semibold bg-white-a700 rounded-full p-1.5 hover:bg-light-angular-gradient"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/probes/${probe.id}`);
                  }}>
                  View details
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col justify-between min-h-[80px] gap-[22px]">
            <div className="flex gap-2">
              <WarningIcon />
              <span className="text-[#E27D55]">No probe attached</span>
            </div>
            <div className="lg:flex lg:justify-end">
              <div className="flex justify-center items-center bg-angular-gradient rounded-full p-0.5 w-[128px]">
                <button
                  type="submit"
                  className="bg-rounded-full w-[128px] h-[34px] text-[14px] font-space-grotesk font-semibold bg-white-a700 rounded-full p-1.5 hover:bg-light-angular-gradient"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/probes/update-probe-details/${user.organizationId}/new?licenseId=${licenseInfo?.id}`
                    );
                  }}>
                  Attach probe
                </button>
              </div>
            </div>
          </div>
        )}
      </section> */}
      <section className="min-h-[100px]">
        <div className="flex md:gap-48 gap-5 flex-col md:flex-row">
          <div>
            <div className="flex gap-2 items-center">
              <span>
                <img src={linkedProbe} className="w-8 h-8" />
              </span>
              <span
                className="font-bold text-xl">Linked Probe</span>
            </div>
            <div className="flex flex-col">
              <span className="mt-2">
                {licenseInfo.device ? (
                  <span className="text-sm">
                    {probe?.externalDeviceId?.startsWith('KB') || probe?.externalDeviceId?.startsWith('BT')
                      ? `${probe?.model?.manufacturer?.name} ${probe?.model?.modelName}`
                      : 'Probe unknown'
                    }
                  </span>
                ) : (
                  <span>No probe linked</span>
                )}
              </span>
              <span>
                {licenseInfo.device ? (<span className="text-sm">{`Serial number: ${probe?.externalDeviceId}`}</span>) : (<span></span>)}
              </span>
            </div>
          </div>
          <div>
            <div className="flex gap-2 items-center">
              <span role="img" aria-label="billing">
                <PaymentsIcon width="31" height="31" />
              </span>
              <span className="font-bold text-xl ml-2">Billing</span>
            </div>
            {/* <div className="mt-2">
              <div className="text-sm">Card ending by { }</div>
              <div className="text-sm flex gap-2">
                <span className="mt-2">Next Payment: {expiryDate}</span>
              </div>
            </div> */}
            <div className="mt-2">
              <div className="text-sm">Card ending by {paymentMethod?.last4 ? `**** ${paymentMethod.last4}` : "****"}</div>
              <div className="text-sm flex gap-2">
                <span className="mt-1">Next Payment: {showNonRenewed ? "-" : expiryDate}</span>
              </div>
            </div>

          </div>
          {
            showNonRenewed ? (
              <div className="md:ml-auto">
                <div className="flex gap-2 items-center">
                  <span role="img" aria-label="billing">
                    <CircleCrossIcon width="20" height="20" />
                  </span>
                  <span className="text-lg font-medium">Non-renewing</span>
                </div>
                {/* <div className="mt-2">
              <div className="text-sm">Card ending by { }</div>
              <div className="text-sm flex gap-2">
                <span className="mt-2">Next Payment: {expiryDate}</span>
              </div>
            </div> */}
                <div className="mt-2">
                  <div className="text-sm">Submitted: {cancelledAt}</div>
                  <div className="text-sm flex gap-2">
                    <span className="mt-1">Effective: {expiryDate}</span>
                  </div>
                </div>

              </div>
            ) : (null)
          }
        </div>
      </section>
    </div>
  );
};

export default LicenseCard;
